import { observer } from 'mobx-react-lite';
import React from 'react';
import { setFocus } from '@noriginmedia/norigin-spatial-navigation';

import ClockNineInfo from '@smart-tv/components/icons/clock-nine-info';
import PlaySentSending from '@smart-tv/components/icons/play-sent-sending';
import TvLine from '@smart-tv/components/icons/tv-line';
import RecordingIndicator from '@smart-tv/components/icons/recording-indicator';
import Typography from '@smart-tv/components/typography';

import { formatMaxCharsWords } from '../../../utils/formatter/format-max-chars-words';
import { rootStore } from '../_stores/store-root';
import { FOCUSED_EPISODE_MAX_CHARS, FocusKeysSheet } from '../sheet-helpers';
import { calculateExpiryContent } from './sheet-description-info-expiry';
import { renderAirtime } from './sheet-description-info-airtime';
import EpisodeDescriptionReadMore from './sheet-episode-description-readMore';
import SheetEpisodeSelectorDetailSkeleton from './sheet-episode-selector-detail-skeleton';
import SheetStringIcons from './sheet-string-icons';
import styles from '../episode-selector.module.scss';

interface SheetEpisodeSelectorDetailProps {
  isVisible?: boolean;
  isLoading?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const SheetEpisodeSelectorDetail = observer(
  ({
    isVisible = true,
    isLoading = false,
    onMouseEnter,
    onMouseLeave
  }: SheetEpisodeSelectorDetailProps) => {
    const { episodes, focusedEpisode, nextEventForSeries, fetchGeneration } =
      rootStore.episodesStore;

    const handleReadMoreArrowPress = React.useCallback((direction: string) => {
      if (direction === 'up') {
        setFocus(FocusKeysSheet.EPISODE_SELECTOR_LIST);
        return false;
      }

      if (direction === 'left') {
        return false;
      }

      return true;
    }, []);

    const episode =
      focusedEpisode.episodeId !== 0
        ? focusedEpisode.isNextEventforSeries
          ? nextEventForSeries?.episodeId === focusedEpisode.episodeId
            ? nextEventForSeries
            : null
          : episodes.find(
              (ep) =>
                ep.episodeId === focusedEpisode.episodeId &&
                ep.isNextEventforSeries === focusedEpisode.isNextEventforSeries
            )
        : null;
    if (isLoading) {
      return <SheetEpisodeSelectorDetailSkeleton />;
    }

    if (!episode) return null;

    const provider = episode.providers[0];
    const version = provider?.versions[0];

    const upcomingAirTime =
      episode.isNextEventforSeries && version?.airTimeStart
        ? renderAirtime(new Date(version.airTimeStart * 1000))
        : undefined;

    const readableAirTimeOrRecordingTime =
      version?.readableAirTimeOrRecordingTime ??
      episode.readableAirTimeOrRecordingTime ??
      upcomingAirTime ??
      undefined;

    const unPublishDate = version?.unPublishDate;
    const expiring = typeof unPublishDate === 'number' ? unPublishDate * 1000 : 0;
    const expiresWithin30Days = expiring ? expiring - Date.now() < 31 * 24 * 60 * 60 * 1000 : false;

    const isRecording = Boolean(version?.npvrInfo?.customerRecording);
    const isNextEventIcon = episode.isNextEventforSeries ? TvLine : PlaySentSending;
    const airTimeIconComponent = isRecording
      ? RecordingIndicator
      : readableAirTimeOrRecordingTime
      ? isNextEventIcon
      : undefined;

    const missingProviderText = episode.providerAccessNeededText ?? '';

    const episodeDescription = episode.longDescription
      ? episode.longDescription
          .split('\n')
          .map((line: string) =>
            formatMaxCharsWords({ type: 'CHARS', max: FOCUSED_EPISODE_MAX_CHARS, input: line })
          )
          .join(' ')
      : '';

    if (!isVisible) {
      return null;
    }

    return (
      <div
        className={styles['episode-detail']}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <EpisodeDescriptionReadMore onArrowPress={handleReadMoreArrowPress} />
        <div className={styles['episode-detail__content']}>
          {episode.providers.length >= 1 && (
            <Typography variant="caption2" className={styles['episode-detail__meta']}>
              <SheetStringIcons
                className={styles['episode-detail__meta-icons']}
                part1={provider?.providerName}
                IconComponent1={airTimeIconComponent}
                part2={readableAirTimeOrRecordingTime}
                IconComponent2={expiresWithin30Days && expiring ? ClockNineInfo : undefined}
                part3={
                  expiresWithin30Days && expiring
                    ? calculateExpiryContent(expiring, true)
                    : undefined
                }
                needSubscription={!episode.hasProviderAccess && !episode.isNextEventforSeries}
              />
            </Typography>
          )}
          {!episode.hasProviderAccess && missingProviderText && (
            <Typography variant="body" className={styles['episode-detail__missing-access']}>
              {missingProviderText.split('\n').map((line: string, index: number) => (
                <span key={index}>
                  {line}
                  <br />
                </span>
              ))}
            </Typography>
          )}
          {episodeDescription && (
            // Keyed on fetchGeneration (not just the text) so a remount is forced on every
            // refresh cycle - the -webkit-line-clamp truncation doesn't reliably re-apply when
            // the element is merely patched in place, even if the refetched text is unchanged.
            <Typography
              key={`${episode.episodeId}-${fetchGeneration}`}
              variant="body"
              className={styles['episode-detail__description']}
            >
              {episodeDescription}
            </Typography>
          )}
        </div>
      </div>
    );
  }
);

SheetEpisodeSelectorDetail.displayName = 'SheetEpisodeSelectorDetail';

export default SheetEpisodeSelectorDetail;
